export class CacheManager {
  static clearAllCache(): void {
    try {
      localStorage.clear();
      sessionStorage.clear();
      this.clearIndexedDB();
      this.clearBrowserCache();
      this.clearServiceWorkerCaches();
    } catch (error) {
      console.error('Error clearing cache');
    }
  }

  /**
   * Clear user-specific cache data
   * @param userId - User ID to clear specific user data
   */
  static clearUserCache(userId: number): void {
    try {
      // Clear user-specific localStorage keys
      const userKeys = [
        `generatedSession_${userId}`,
        `vision_${userId}`,
        'nuroleap_token',
        'nuroleap_user_id',
        'nuroleap_last_login'
      ];

      userKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      console.log(`User cache cleared for user`);
    } catch (error) {
      console.error('Error clearing user cache');
    }
  }

  /**
   * Clear authentication-related cache
   */
  static clearAuthCache(): void {
    try {
      const authKeys = [
        'nuroleap_token',
        'nuroleap_user_id',
        'nuroleap_last_login'
      ];

      authKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing auth cache:', error);
    }
  }

  /**
   * Clear session-related cache
   * @param userId - User ID to clear session data
   */
  static clearSessionCache(userId: number): void {
    try {
      const sessionKeys = [
        `generatedSession_${userId}`,
        `vision_${userId}`
      ];

      sessionKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing session');
    }
  }

  /**
   * Clear IndexedDB data (if used)
   */
  private static clearIndexedDB(): void {
    try {
      if ('indexedDB' in window) {
        const databases = ['nuroleap-cache', 'nuroleap-sessions', 'nuroleap-audio'];

        databases.forEach(dbName => {
          const deleteReq = indexedDB.deleteDatabase(dbName);
          deleteReq.onsuccess = () => {
            console.log(`Index cleared`);
          };
          deleteReq.onerror = () => {
            console.warn(`Failed to clear`);
          };
        });
      }
    } catch (error) {
      console.warn('Error clearing IndexedDB:', error);
    }
  }

  /**
   * Clear browser cache (if possible)
   */
  private static clearBrowserCache(): void {
    try {
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
          });
        });
      }
    } catch (error) {
      console.warn('Error clearing browser cache:', error);
    }
  }

  /**
   * Clear service worker caches
   */
  private static clearServiceWorkerCaches(): void {
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister();
          });
        });
      }
    } catch (error) {
      console.warn('Error clearing service worker caches:', error);
    }
  }

  /**
   * Force page reload to clear any remaining memory cache
   */
  static forceReload(): void {
    try {
      window.location.reload();
    } catch (error) {
      console.error('Error during force reload:', error);
    }
  }

  /**
   * Complete logout with cache clearing and redirect
   * @param redirectPath - Path to redirect after logout (default: '/login')
   */
  static completeLogout(redirectPath: string = '/login'): void {
    try {
      this.clearAllCache();

      setTimeout(() => {
        window.location.href = redirectPath;
      }, 100);

    } catch (error) {
      console.error('Error during complete logout');
      window.location.href = redirectPath;
    }
  }
}
